import React from 'react'
import { AnalysisMainMetricCard } from '../../../../../atoms/AnalysisMainMetricCard'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import { Grid } from '../../../BuildingCharacteristics.styles'
import {
  SIZE_THRESHOLDS,
  HEIGHT_THRESHOLDS,
  AGE_THRESHOLDS,
  FLOOR_COUNT_THRESHOLD,
  ICON_COLORS,
} from '../OverviewTab.constants'
import type { HeroMetricsGridProps } from '../OverviewTab.types'

export const HeroMetricsGrid: React.FC<HeroMetricsGridProps> = ({
  footprintArea,
  floorCount,
  height,
  buildingAge,
  isAgeEstimate,
  buildingPeriod,
  t,
}) => {
  const formatArea = (area?: number) =>
    area ? `${Math.round(area).toLocaleString()} m²` : t('common.na')
  const formatHeight = (h?: number) =>
    h ? `${h.toFixed(1)} m` : t('common.na')

  return (
    <Grid $columns={4}>
      <AnalysisMainMetricCard
        iconName="square"
        iconBackgroundColor={ICON_COLORS.AREA}
        value={formatArea(footprintArea)}
        label={
          <>
            {t('overview.metrics.buildingArea.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('overview.metrics.buildingArea.tooltip.title')}
                shortDescription={t(
                  'overview.metrics.buildingArea.tooltip.description'
                )}
                calculation={t(
                  'overview.metrics.buildingArea.tooltip.calculation'
                )}
                importance={t(
                  'overview.metrics.buildingArea.tooltip.importance'
                )}
                limitations={t(
                  'overview.metrics.buildingArea.tooltip.limitations'
                )}
                source={t('overview.metrics.buildingArea.tooltip.source')}
              />
            </InfoTooltip>
          </>
        }
        variant={{
          type: 'highlight',
          highlightType:
            footprintArea > SIZE_THRESHOLDS.LARGE
              ? 'success'
              : footprintArea > SIZE_THRESHOLDS.MEDIUM
                ? 'warning'
                : 'info',
          text:
            footprintArea > SIZE_THRESHOLDS.LARGE
              ? t('overview.sizeIndicators.large')
              : footprintArea > SIZE_THRESHOLDS.MEDIUM
                ? t('overview.sizeIndicators.medium')
                : t('overview.sizeIndicators.compact'),
        }}
      />

      <AnalysisMainMetricCard
        iconName="layers"
        iconBackgroundColor={ICON_COLORS.FLOORS}
        value={floorCount || t('common.na')}
        label={
          <>
            {t('overview.metrics.floorCount.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('overview.metrics.floorCount.tooltip.title')}
                shortDescription={t(
                  'overview.metrics.floorCount.tooltip.description'
                )}
                calculation={t(
                  'overview.metrics.floorCount.tooltip.calculation'
                )}
                importance={t('overview.metrics.floorCount.tooltip.importance')}
                limitations={t(
                  'overview.metrics.floorCount.tooltip.limitations'
                )}
                source={t('overview.metrics.floorCount.tooltip.source')}
              />
            </InfoTooltip>
          </>
        }
        variant={{
          type: 'progressBar',
          percentage: Math.min(
            (floorCount / FLOOR_COUNT_THRESHOLD.MAX_PROGRESS) * 100,
            100
          ),
          color:
            floorCount >= FLOOR_COUNT_THRESHOLD.HIGH_RISE
              ? '#3b82f6'
              : '#10b981',
        }}
      />

      <AnalysisMainMetricCard
        iconName="arrow-up"
        iconBackgroundColor={ICON_COLORS.HEIGHT}
        value={formatHeight(height)}
        label={
          <>
            {t('overview.metrics.buildingHeight.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('overview.metrics.buildingHeight.tooltip.title')}
                shortDescription={t(
                  'overview.metrics.buildingHeight.tooltip.description'
                )}
                calculation={t(
                  'overview.metrics.buildingHeight.tooltip.calculation'
                )}
                importance={t(
                  'overview.metrics.buildingHeight.tooltip.importance'
                )}
                limitations={t(
                  'overview.metrics.buildingHeight.tooltip.limitations'
                )}
                source={t('overview.metrics.buildingHeight.tooltip.source')}
              />
            </InfoTooltip>
          </>
        }
        variant={{
          type: 'highlight',
          highlightType:
            height > HEIGHT_THRESHOLDS.HIGH_RISE
              ? 'info'
              : height > HEIGHT_THRESHOLDS.MID_RISE
                ? 'success'
                : 'warning',
          text:
            height > HEIGHT_THRESHOLDS.HIGH_RISE
              ? t('overview.heightTypes.highRise')
              : height > HEIGHT_THRESHOLDS.MID_RISE
                ? t('overview.heightTypes.midRise')
                : t('overview.heightTypes.lowRise'),
        }}
      />

      <AnalysisMainMetricCard
        iconName="calendar"
        iconBackgroundColor={ICON_COLORS.AGE}
        value={
          buildingAge > 0 ? (
            <>
              {buildingAge} {t('common.years')}
              {isAgeEstimate && (
                <span
                  style={{
                    fontSize: '11px',
                    opacity: 0.7,
                    marginLeft: '4px',
                  }}
                >
                  ({t('overview.labels.estimated')})
                </span>
              )}
            </>
          ) : (
            t('common.na')
          )
        }
        label={
          <>
            {t('overview.metrics.buildingAge.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('overview.metrics.buildingAge.tooltip.title')}
                shortDescription={t(
                  'overview.metrics.buildingAge.tooltip.description'
                )}
                calculation={
                  isAgeEstimate
                    ? t(
                        'overview.metrics.buildingAge.tooltip.calculationEstimated',
                        { period: buildingPeriod }
                      )
                    : t('overview.metrics.buildingAge.tooltip.calculation')
                }
                categories={[
                  {
                    label: t('overview.ageCategories.new.label'),
                    range: t('overview.ageCategories.new.range'),
                    description: t('overview.ageCategories.new.description'),
                  },
                  {
                    label: t('overview.ageCategories.contemporary.label'),
                    range: t('overview.ageCategories.contemporary.range'),
                    description: t(
                      'overview.ageCategories.contemporary.description'
                    ),
                  },
                  {
                    label: t('overview.ageCategories.mature.label'),
                    range: t('overview.ageCategories.mature.range'),
                    description: t('overview.ageCategories.mature.description'),
                  },
                  {
                    label: t('overview.ageCategories.historic.label'),
                    range: t('overview.ageCategories.historic.range'),
                    description: t(
                      'overview.ageCategories.historic.description'
                    ),
                  },
                ]}
                importance={t(
                  'overview.metrics.buildingAge.tooltip.importance'
                )}
                limitations={
                  isAgeEstimate
                    ? t(
                        'overview.metrics.buildingAge.tooltip.limitationsEstimated'
                      )
                    : t('overview.metrics.buildingAge.tooltip.limitations')
                }
                source={t('overview.metrics.buildingAge.tooltip.source')}
              />
            </InfoTooltip>
          </>
        }
        variant={{
          type: 'highlight',
          highlightType:
            buildingAge < AGE_THRESHOLDS.NEW
              ? 'success'
              : buildingAge < AGE_THRESHOLDS.CONTEMPORARY
                ? 'info'
                : buildingAge < AGE_THRESHOLDS.MATURE
                  ? 'warning'
                  : 'danger',
          text:
            buildingAge < AGE_THRESHOLDS.NEW
              ? t('overview.ageCategories.new.label')
              : buildingAge < AGE_THRESHOLDS.CONTEMPORARY
                ? t('overview.ageCategories.contemporary.label')
                : buildingAge < AGE_THRESHOLDS.MATURE
                  ? t('overview.ageCategories.mature.label')
                  : t('overview.ageCategories.historic.label'),
        }}
      />
    </Grid>
  )
}
